// Helpers
import { DateTime } from 'luxon';
import {
  cleanPhoneNumber,
  to,
  stripDashes,
  convertNullsToEmptyStrings,
} from '../../lib/helpers';
import {
  bodyHasError,
  checkRequestFailure,
  handleRequestFailure,
  ERRORS_MAP,
  errorsWithOnlyCodes,
} from '../../lib/validation';
import { gaActions } from '../gaActionTypes';
import { selectSignInParamValue, selectDeviceId } from '../auth/authSelectors';
import CONSTANTS from '../../lib/data/constants';

// Requests
import {
  userExistsRequest,
  userVerifyAddressRequest,
} from '../../backend/userRequests';
import {
  createAccountAddUserRequest,
  createAccountIDologyQuestionsAnsweredRequest,
  createAccountVerifyUserRequest,
  createAccountCreateUserRequest,
} from '../../backend/createAccountRequests';

// Actions
import { storeEmail, updateLoginMessage } from '../login/loginActions';
import { clearAlertInfo } from '../alert/alertActions';
import { closeCurrentModal, openModal } from '../modal/modalActions';
import {
  sendOTP,
  verifyOTPCreateAccount,
  clearOTPState,
  OTP_MESSAGE_TYPES,
} from '../OTP/OTPActions';
import { getDeviceId, getOOSDeviceId } from '../auth/authActions';
import { push } from 'connected-react-router';
import * as errorActions from '../error/errorActions';
import * as backend from '../../backend';

// Consts
export const CREATE_ACCOUNT_UPDATE_STATE = '[CREATE ACCOUNT] Update State';
export const CREATE_ACCOUNT_CAPTURE_EMAIL_USER_EXISTS =
  '[CREATE ACCOUNT] Email Exists';
export const CREATE_ACCOUNT_CAPTURE_EMAIL_AWAITING_CODE = `[CREATE ACCOUNT] ${gaActions.submit} Awaiting Email OTP Code`;
export const CREATE_ACCOUNT_RECAPTURE_EMAIL = `[CREATE ACCOUNT] ${gaActions.back} Recapture Email`;
export const CREATE_ACCOUNT_CAPTURE_EMAIL_VERIFIED = `[CREATE ACCOUNT] ${gaActions.submit} Email OTP Code Success`;
export const CREATE_ACCOUNT_CAPTURE_EMAIL_VERIFIED_ERROR =
  '[CREATE ACCOUNT] Email OTP Code Error';
export const CREATE_ACCOUNT_CLEAR_EMAIL_STATE =
  '[CREATE ACCOUNT] Clear Email State';
export const CREATE_ACCOUNT_CAPTURE_PASSWORD = `[CREATE ACCOUNT] ${gaActions.submit} Capture Password`;
export const CREATE_ACCOUNT_CAPTURE_PHONE = `[CREATE ACCOUNT] ${gaActions.submit} Capture Phone`;
export const CREATE_ACCOUNT_RECAPTURE_PHONE = `[CREATE ACCOUNT] ${gaActions.back} Recapture Phone`;
export const CREATE_ACCOUNT_CAPTURE_PHONE_VERIFIED = `[CREATE ACCOUNT] ${gaActions.submit} Phone OTP Code Success`;
export const CREATE_ACCOUNT_CAPTURE_IDENTITY_PATH_SELECTED = `[CREATE ACCOUNT] ${gaActions.submit} Select Capture Identity Path`;
export const CREATE_ACCOUNT_TOAS_FAILED = `[CREATE ACCOUNT] ${gaActions.failure} TOAS Failure`;
export const CREATE_ACCOUNT_CAPTURE_IDENTITY = `[CREATE ACCOUNT] ${gaActions.submit} Capture Identity`;
export const CREATE_ACCOUNT_CAPTURE_HOME_ADDRESS = `[CREATE ACCOUNT] ${gaActions.submit} Capture Home Address`;
export const CREATE_ACCOUNT_CAPTURE_IDENTITY_QUESTIONS =
  '[CREATE ACCOUNT] Capture Identity Questions';
export const CREATE_ACCOUNT_SKIP_THIS_QUESTION =
  '[CREATE ACCOUNT] Skip This Question';
export const CREATE_ACCOUNT_CAPTURE_IDENTITY_ANSWER =
  '[CREATE ACCOUNT] Capture Identity Answer';
export const CREATE_ACCOUNT_CLEAR_IDOLOGY_QUESTIONS =
  '[CREATE ACCOUNT] Clear IDology Questions';
export const CREATE_ACCOUNT_CLEAR_DATA = '[CREATE ACCOUNT] Clear Data';
export const CREATE_ACCOUNT_SET_OUT_OF_STATE =
  '[CREATE ACCOUNT] Set Out Of State';
export const CREATE_ACCOUNT_SUCCESS = `[CREATE ACCOUNT] ${gaActions.submit} Success`;
export const CREATE_ACCOUNT_FAILED = '[CREATE ACCOUNT] Failed';
export const CREATE_ACCOUNT_FAILED_IDOLOGY = '[CREATE ACCOUNT IDOLOGY] Failed';
export const CREATE_ACCOUNT_FAILED_TOAS = '[CREATE ACCOUNT TOAS] Failed';
export const CREATE_ACCOUNT_BLOCKED = '[CREATE ACCOUNT] Blocked';
export const CREATE_ACCOUNT_LOCKED = '[CREATE ACCOUNT] Locked';
export const CREATE_ACCOUNT_STEPS = {
  START: 'start',
  EMAIL: 'email',
  PASSWORD: 'password',
  PHONE: 'phone',
  IDENTITY: 'identity',
  HOME_ADDRESS: 'home-address',
  SUCCESS: 'success',
  FAILED_IDOLOGY: 'idology-failed',
  FAILED_TOAS: 'toas-failed',
  FAILED_IDPROOFING: 'idproof-failed',
  IDPROOFING_MAX_ATTEMPTS: 'idproof-attempts-exceeded',
  ACCOUNT_DEFINITELY_EXISTS: 'account-exists',
  ACCOUNT_MAY_EXIST: 'account-may-exist',
  CREATE_ACCOUNT_SUCCESS_LINKING_FAILED: 'linking-failed',
  BLOCKED: 'blocked',
  LOCKED: 'locked',
  DISABLED_USER: 'disabled-user',
};
export const CREATE_ACCOUNT_IDENTITY_STEPS = {
  CHOOSE_PATH: 'choose-path',
  TOAS: 'toas',
  IDOLOGY: 'idology',
  QUESTIONS: 'questions',
};
export const CREATE_ACCOUNT_EMAIL_STEPS = {
  CAPTURE: 'capture',
  VERIFY: 'verify',
};
export const CREATE_ACCOUNT_PHONE_STEPS = {
  CAPTURE: 'capture',
  VERIFY: 'verify',
};
export const CREATE_ACCOUNT_WITH_LINK_DLID = '[CREATE ACCOUNT] Link DLID';
export const CREATE_ACCOUNT_QUESTIONS_FAILED = `[CREATE ACCOUNT] Failure`;
export const CREATE_ACCOUNT_CAPTURE_VERIFY = `[CREATE ACCOUNT] ${gaActions.success} Capture Verify`;

// STRY0286126 - Added new action type for OOS flow
const getHasTxDLID = pii => {
  if (pii.hasTxDL === 'yes') return true;
  if (pii.hasTxDL === 'no') return false;
  return !!(pii.dlNumber && pii.auditNum);
};

//Helper to generate unique paths for No TX DL/ID flows
const getCreateAccountPath = (stepId, pii) => {
  const isNoTxDLPath = pii && pii.hasTxDL === 'no';
  const path = `/create-account/${stepId}`;
  return isNoTxDLPath ? `${path}/no-txdl` : path;
};

export const skipThisQuestion = skippedIndex => dispatch =>
  dispatch({ type: CREATE_ACCOUNT_SKIP_THIS_QUESTION, payload: skippedIndex });

export const setOutOfState = isOutOfState => dispatch =>
  dispatch({ type: CREATE_ACCOUNT_SET_OUT_OF_STATE, payload: isOutOfState });

export const captureEmailSubmitRequested = ({
  values: { email },
  formikActions,
}) => async dispatch => {
  const [{ response, body }] = await to(dispatch(userExistsRequest(email)));

  const failure = errorsWithOnlyCodes(((body && body.errors) || {}).ERROR);
  const userDeactivated = failure.includes('MGMW_E_0001');

  if (!userDeactivated && checkCreateAccountFailure(failure))
    return dispatch(handleCreateAccountFailure(failure, formikActions));

  if (!userDeactivated && checkRequestFailure({ response, body }))
    return handleRequestFailure(body, formikActions, ERRORS_MAP.USER_EXISTS);

  if (body && body.message && body.message.toLowerCase() === 'user exists') {
    formikActions.setSubmitting(false);
    dispatch(openModal('AccountExistsModal'));
    return dispatch({
      type: CREATE_ACCOUNT_CAPTURE_EMAIL_USER_EXISTS,
      payload: { email },
    });
  }

  if (userDeactivated || body.message.toLowerCase() === 'user not found') {
    const [{ body: OTPbody, response: OTPresponse }] = await to(
      dispatch(
        sendOTP({
          userEmail: email,
          contactInfo: email,
          messageType: OTP_MESSAGE_TYPES.CREATE_ACCOUNT,
          channelCode: 'SMTP',
          typeCode: 'PE',
        })
      )
    );

    if (checkRequestFailure({ response: OTPresponse, body: OTPbody })) {
      return handleRequestFailure(OTPbody, formikActions);
    }

    return dispatch({
      type: CREATE_ACCOUNT_CAPTURE_EMAIL_AWAITING_CODE,
      payload: { email },
    });
  }
};

export const verifyEmailCodeSubmitRequested = ({
  values: { code },
  props: {
    OTP: { data, headers },
  },
  formikActions,
}) => async (dispatch, getState) => {
  headers['user-email'] = data.userEmail;

  const [{ response, body }] = await to(
    dispatch(
      verifyOTPCreateAccount({ userEmail: data.userEmail, code, headers })
    )
  );

  if (checkRequestFailure({ response, body })) {
    return handleRequestFailure(body, formikActions, ERRORS_MAP.VERIFY_OTP);
  }

  if (body.status === 'SUCCESS') {
    dispatch(clearOTPState());
    dispatch({
      type: CREATE_ACCOUNT_CAPTURE_EMAIL_VERIFIED,
      payload: body.data,
    });
    const pii = getState().createAccount.pii;
    return dispatch(
      push(getCreateAccountPath(CREATE_ACCOUNT_STEPS.PASSWORD, pii))
    );
  }
};

export const recaptureEmail = () => dispatch =>
  dispatch({ type: CREATE_ACCOUNT_RECAPTURE_EMAIL });

export const clearEmailState = () => dispatch =>
  dispatch({ type: CREATE_ACCOUNT_CLEAR_EMAIL_STATE });

export const capturePassword = ({ values }) => (dispatch, getState) => {
  dispatch({ type: CREATE_ACCOUNT_CAPTURE_PASSWORD, payload: values.password });
  const pii = getState().createAccount.pii;
  return dispatch(push(getCreateAccountPath(CREATE_ACCOUNT_STEPS.PHONE, pii)));
};

export const captureMobilePhone = ({
  values: { phoneNum },
  formikActions,
}) => async (dispatch, getState) => {
  const {
    createAccount: { user },
  } = getState();

  const [{ body: OTPbody, response: OTPresponse }] = await to(
    dispatch(
      sendOTP({
        userEmail: user.userEmail,
        contactInfo: cleanPhoneNumber(phoneNum),
        messageType: OTP_MESSAGE_TYPES.CREATE_ACCOUNT,
        channelCode: 'SMS',
        typeCode: 'PM',
      })
    )
  );

  if (checkRequestFailure({ response: OTPresponse, body: OTPbody })) {
    return handleRequestFailure(OTPbody, formikActions);
  }

  return dispatch({
    type: CREATE_ACCOUNT_CAPTURE_PHONE,
    payload: { phoneNum: cleanPhoneNumber(phoneNum) },
  });
};

export const verifyMobilePhoneCode = ({ payload, formikActions }) => async (
  dispatch,
  getState
) => {
  const { code, otp } = payload;
  const {
    data: { contactInfo },
    headers,
  } = otp;

  const {
    createAccount: { user },
  } = getState();

  headers['user-email'] = user.userEmail;
  const [{ response, body }] = await to(
    dispatch(verifyOTPCreateAccount({ userPhone: contactInfo, code, headers }))
  );

  if (checkRequestFailure({ response, body })) {
    return handleRequestFailure(body, formikActions, ERRORS_MAP.VERIFY_OTP);
  }

  if (body.status === 'SUCCESS') {
    dispatch(clearOTPState());
    dispatch({
      type: CREATE_ACCOUNT_CAPTURE_PHONE_VERIFIED,
      payload: body.data,
    });

    dispatch(
      push(`/create-account/identity/${CREATE_ACCOUNT_IDENTITY_STEPS.TOAS}`)
    );
  }
};

export const recaptureMobilePhone = () => dispatch => {
  return dispatch({ type: CREATE_ACCOUNT_RECAPTURE_PHONE });
};

export const selectIdentityPath = payload => dispatch => {
  dispatch({ type: CREATE_ACCOUNT_CAPTURE_IDENTITY_PATH_SELECTED, payload });
  return payload === 'TOAS'
    ? dispatch(
        push(`/create-account/identity/${CREATE_ACCOUNT_IDENTITY_STEPS.TOAS}`)
      )
    : dispatch(
        push(
          `/create-account/identity/${CREATE_ACCOUNT_IDENTITY_STEPS.IDOLOGY}`
        )
      );
};

export const captureIdentityTOAS = ({
  values: { hasTxDL, fName, lName, dlNumber, auditNum, dob, ssn, terms },
}) => async (dispatch, getState) => {
  const { createAccount } = getState();
  const user = { ...createAccount.user, fName, lName };
  const pii = {
    ...createAccount.pii,
    hasTxDL,
    dlNumber,
    auditNum,
    dob,
    ssn: stripDashes(ssn),
    terms,
  };

  // hasTxDLID is true only when user selected "yes" for TX DL/ID.
  // Fallback to DL/Audit presence for older state shapes.
  const hasTxDLID = getHasTxDLID(pii);

  // Set out-of-state flag based on whether user has TX DL/ID
  if (!hasTxDLID) {
    await dispatch(setOutOfState(true));
  } else {
    await dispatch(setOutOfState(false));
  }

  await dispatch({
    type: CREATE_ACCOUNT_UPDATE_STATE,
    payload: {
      user,
      pii,
      acceptTos: terms ? DateTime.utc() : null,
    },
  });

  dispatch(push(getCreateAccountPath(CREATE_ACCOUNT_STEPS.HOME_ADDRESS, pii)));
};

export const captureIdentityIDology = ({
  values: {
    fName,
    lName,
    streetAddress,
    addressLine2,
    city,
    state,
    zipFive,
    verifySkip,
    dob,
    lastFourSSN,
  },
  props: {
    createAccount: {
      pii: { auditNum, dlNumber, ...existingPii },
      user: existingUser,
    },
  },
  formikActions,
}) => async (dispatch, getState) => {
  const user = { ...existingUser, fName, lName };
  const pii = {
    ...existingPii,
    address: {
      streetAddress,
      addressLine2,
      city,
      state: state.value,
      zipFive,
      verifySkip,
    },
    dob,
    lastFourSSN,
  };
  const acceptTos = DateTime.utc();
  const userFlow = selectSignInParamValue(getState());
  const [{ response, body }] = await to(
    dispatch(createAccountAddUserRequest({ user, pii, acceptTos, userFlow }))
  );

  dispatch({
    type: CREATE_ACCOUNT_UPDATE_STATE,
    payload: { user, pii: { ...pii, auditNum, dlNumber } },
  });

  const failure = errorsWithOnlyCodes(((body && body.errors) || {}).ERROR);

  if (checkCreateAccountFailure(failure))
    return dispatch(handleCreateAccountFailure(failure, formikActions));

  if (checkRequestFailure({ response, body }))
    return handleRequestFailure(body, formikActions, {
      ...ERRORS_MAP.CREATE_ACCOUNT_IDENTITY,
      streetAddress: 'pii.address.streetAddress',
      addressLine2: 'pii.address.addressLine2',
      city: 'pii.address.city',
      'state.value': 'pii.address.state',
      zipFive: 'pii.address.zipFive',
    });

  if (body.status === 'CONTINUE') {
    formikActions.setSubmitting(false);
    return dispatch(
      openModal('VerifyAddressModal/IDology', {
        enteredAddress: pii.address,
        addressOptions: body.data,
      })
    );
  }

  if (body.data && body.data.questions && body.data.questions.length > 0) {
    dispatch(clearAlertInfo());
    dispatch({
      type: CREATE_ACCOUNT_UPDATE_STATE,
      payload: { userInformation: { user, pii, acceptTos } },
    });
    dispatch({
      type: CREATE_ACCOUNT_CAPTURE_IDENTITY_QUESTIONS,
      payload: body,
    });
    return dispatch(
      push(
        `/create-account/identity/${CREATE_ACCOUNT_IDENTITY_STEPS.QUESTIONS}/1`
      )
    );
  }

  if (body.status === 'SUCCESS')
    return dispatch(
      handleCreateAccountSuccess({
        email: user.userEmail,
        firstName: user.fName,
        reactivated: body.message === 'REACTIVATED',
      })
    );
};

export const createAccountCaptureIdentityAnswer = ({
  values: { answer, questionNumber },
  props: { createAccount },
  formikActions,
}) => async (dispatch, getState) => {
  const { IDology, userInformation } = createAccount;
  formikActions.setSubmitting(false);

  dispatch({
    type: CREATE_ACCOUNT_CAPTURE_IDENTITY_ANSWER,
    payload: { answer, questionNumber },
  });

  if (questionNumber < IDology.questions.length) {
    const pii = getState().createAccount.pii;
    dispatch(
      push(
        getCreateAccountPath(`identity/questions/${questionNumber + 1}`, pii)
      )
    );
    formikActions.resetForm();
  }

  if (questionNumber === IDology.questions.length) {
    const data = {
      ...IDology,
      userInformation: {
        ...userInformation,
        userFlow: selectSignInParamValue(getState()),
      },
      questions: [...IDology.questions].map((question, index) => {
        question = {
          ...question,
          answer: [createAccount.identityAnswers[index] || answer],
        };
        return question;
      }),
    };

    const [{ body }] = await to(
      dispatch(createAccountIDologyQuestionsAnsweredRequest(data))
    );

    const failure = errorsWithOnlyCodes(((body && body.errors) || {}).ERROR);

    if (checkCreateAccountFailure(failure))
      return dispatch(handleCreateAccountFailure(failure, formikActions));

    if (body.status === 'SUCCESS')
      return dispatch(
        handleCreateAccountSuccess({
          email: userInformation.user.userEmail,
          firstName: userInformation.user.fName,
          reactivated: body.message === 'REACTIVATED',
        })
      );
  }
};

export const clearCreateAccountData = () => dispatch =>
  dispatch({ type: CREATE_ACCOUNT_CLEAR_DATA });

export const clearIDologyQuestions = () => dispatch =>
  dispatch({ type: CREATE_ACCOUNT_CLEAR_IDOLOGY_QUESTIONS });

export const handleCreateAccountSuccess = ({
  email,
  firstName,
  reactivated,
}) => (dispatch, getState) => {
  const title = reactivated
    ? { file: 'CreateAccount', id: 'WelcomeBack' }
    : { file: 'CreateAccount', id: 'Congratulations', args: [firstName] };

  const signInParam = selectSignInParamValue(getState());

  dispatch({ type: CREATE_ACCOUNT_SUCCESS });
  dispatch(storeEmail(email));
  dispatch(
    updateLoginMessage({
      title,
      info: { file: 'CreateAccount', id: 'AccountSuccessfullyCreated' },
    })
  );

  dispatch(clearAlertInfo());

  return dispatch(
    push(`/sign-in${signInParam ? `?welcome=${signInParam}` : ''}`)
  );
};

const checkCreateAccountFailure = failure =>
  failure.some(
    code =>
      code === 'MGMW_E_0001' ||
      code === 'MGMW_E_0003' ||
      code === 'MGMW_E_0004' ||
      code === 'MGMW_E_0005' ||
      code === 'MGMW_E_0006' ||
      code === 'MGMW_E_0007' ||
      code === 'MGMW_E_0008' ||
      code === 'MGMW_E_0009' ||
      code === 'MGMW_E_0010' ||
      code === 'MGMW_E_0011' ||
      code === 'MGMW_EX_4000' ||
      code === 'MGMW_EX_0000' ||
      code === 'MGMW_EX_0002' ||
      code === 'MGMW_EX_3007' ||
      code === 'MGMW_VE_0000'
  );

export const directToCreateAccountFailure = failure => (dispatch, getState) => {
  let failureType;

  if (failure && failure.includes('MGMW_E_0009')) {
    failureType = 'blocked';
  } else if (
    failure &&
    (failure.includes('MGMW_E_0010') || failure.includes('MGMW_E_0011'))
  ) {
    failureType = 'locked';
  } else if (failure && failure.includes('MGMW_E_0007')) {
    failureType = 'failedIdology';
  } else if (failure && failure.includes('MGMW_E_0004')) {
    failureType = 'failedToas';
  }

  const failureTypes = {
    failedIdology: {
      action: CREATE_ACCOUNT_FAILED_IDOLOGY,
      step: CREATE_ACCOUNT_STEPS.FAILED_IDOLOGY,
    },
    failedToas: {
      action: CREATE_ACCOUNT_FAILED_TOAS,
      step: CREATE_ACCOUNT_STEPS.FAILED_TOAS,
    },
    blocked: {
      action: CREATE_ACCOUNT_BLOCKED,
      step: CREATE_ACCOUNT_STEPS.BLOCKED,
    },
    locked: {
      action: CREATE_ACCOUNT_LOCKED,
      step: CREATE_ACCOUNT_STEPS.LOCKED,
    },
  };
  dispatch(clearAlertInfo());
  dispatch({ type: failureTypes[failureType].action });
  const pii = getState().createAccount.pii;
  return dispatch(
    push(getCreateAccountPath(failureTypes[failureType].step, pii))
  );
};

export const handleCreateAccountFailure = (failure, formikActions) => (
  dispatch,
  getState
) => {
  const displayCouldNotVerifyIdentifyBanner = () => {
    return Promise.reject(failure);
  };

  const handleFailedIdology = () => {
    return displayCouldNotVerifyIdentifyBanner();
  };
  const handleFailedTOAS = () => {
    dispatch({ type: CREATE_ACCOUNT_TOAS_FAILED });
    return displayCouldNotVerifyIdentifyBanner();
  };
  const handleFailed = () => {
    const { identityPath } = getState().createAccount;
    if (identityPath !== 'TOAS') {
      return handleFailedIdology();
    } else {
      return handleFailedTOAS();
    }
  };

  formikActions.setSubmitting(false);
  if (failure.includes('MGMW_E_0003')) {
    return displayCouldNotVerifyIdentifyBanner();
  } else if (
    failure.includes('MGMW_E_0004') ||
    (failure.includes('MGMW_E_0007') &&
      !/questions/.test(window.location.pathname))
  ) {
    return handleFailed();
  } else {
    dispatch(directToCreateAccountFailure(failure));
  }
};

export const captureHomeAddress = ({
  values,
  formikActions,
}) => async dispatch => {
  const payload = convertNullsToEmptyStrings(values);

  if (values.verifySkip) {
    await dispatch({
      type: CREATE_ACCOUNT_CAPTURE_HOME_ADDRESS,
      payload,
    });
    dispatch(closeCurrentModal());
    return dispatch(verifyUser(formikActions));
  }

  const [{ response, body }] = await to(
    dispatch(userVerifyAddressRequest(payload))
  );

  if (body.status === 'ERROR' && !bodyHasError(body)) {
    // If Melissa data returns INVALID_ADDRESS or ADDRESS_NOT_VERIFIED, continue with the address as entered.
    if (['INVALID_ADDRESS', 'ADDRESS_NOT_VERIFIED'].includes(body.message)) {
      body.status = 'SUCCESS';
      body.data = [payload];
    } else {
      const errorId = response.headersObject['x-req-id'];
      body.errors = {
        ...(errorId && { REF: [errorId] }),
        ERROR: ['CREATE_ACCOUNT_ERROR'],
      };
    }
  }

  if (checkRequestFailure({ response, body })) {
    return handleRequestFailure(body, formikActions, ERRORS_MAP.ADDRESS);
  }

  if (body.status === 'CONTINUE') {
    formikActions.setSubmitting(false);
    return dispatch(
      openModal('VerifyAddressModal/CreateAccountPage', {
        enteredAddress: payload,
        addressOptions: body.data,
      })
    );
  }

  if (body.status === 'WARNING') {
    formikActions.setSubmitting(false);
    return dispatch(
      openModal('AddressTwoMissingModal/CreateAccountPage', {
        enteredAddress: payload,
        addressOptions: body.data,
      })
    );
  }

  if (body.status === 'SUCCESS') {
    body.data[0].verifySkip = true;
    await dispatch({
      type: CREATE_ACCOUNT_CAPTURE_HOME_ADDRESS,
      payload: body.data[0],
    });
    dispatch(closeCurrentModal());
    return dispatch(verifyUser(formikActions));
  }
};

export const verifyUser = formikActions => async (dispatch, getState) => {
  const { user, pii, acceptTos, homeAddress } = getState().createAccount;
  // STRY0280371 - hasTxDLID is true only when user selected "yes" for TX DL/ID.
  // Fallback to DL/Audit presence for older state shapes.
  const hasTxDLID = getHasTxDLID(pii);
  const piiWithoutHasTxDL = { ...pii };
  delete piiWithoutHasTxDL.hasTxDL;

  let deviceId = selectDeviceId(getState());

  // Ensure device token exists before verifyUser call
  if (!deviceId) {
    try {
      if (!hasTxDLID) {
        await dispatch(getOOSDeviceId());
      } else {
        const { socureKeys } = getState().auth;
        const accountCreationKey = Array.isArray(socureKeys)
          ? socureKeys.find(
              ({ name }) => name === CONSTANTS.SOCURE_ACCOUNT_CREATION_KEY_NAME
            )
          : null;

        if (accountCreationKey && accountCreationKey.value) {
          await dispatch(getDeviceId(accountCreationKey.value));
        } else {
          await dispatch(getDeviceId());
        }
      }

      deviceId = selectDeviceId(getState());
    } catch {
      return Promise.reject(new Error('Failed to initialize userDeviceToken'));
    }
  }

  if (!deviceId) {
    return Promise.reject(new Error('Failed to initialize userDeviceToken'));
  }

  const payload = {
    user: {
      mName: '',
      displayName: '',
      ...user,
      hasTxDLID,
      userDeviceToken: deviceId,
    },
    pii: {
      ...piiWithoutHasTxDL,
      address: homeAddress,
    },
    acceptTos,
  };

  const [{ response, body }, err] = await to(
    dispatch(createAccountVerifyUserRequest(payload))
  );

  if (err) {
    return Promise.reject(err);
  }

  if (backend.isServerError({ body, response })) {
    return { type: errorActions.SERVER_ERROR, payload: body };
  }

  if (body.status === 'ERROR' && !bodyHasError(body)) {
    const errorId = response.headersObject['x-req-id'];
    body.errors = {
      ...(errorId && { REF: [errorId] }),
      ERROR: ['CREATE_ACCOUNT_ERROR'],
    };
  }

  if (body.status === 'SUCCESS' && body.data && body.data.maskedEmail) {
    body.errors = {
      ERROR: ['MGMW_E_0002'],
    };
  }

  if (checkRequestFailure({ response, body })) {
    const errorCodes = errorsWithOnlyCodes(((body && body.errors) || {}).ERROR);

    let errorPage;
    let state;
    if (
      errorCodes.includes('MGMW_E_0012') ||
      errorCodes.includes('MGMW_E_0004')
    ) {
      errorPage = CREATE_ACCOUNT_STEPS.FAILED_IDPROOFING;
    }
    if (
      errorCodes.includes('MGMW_E_0027') ||
      errorCodes.includes('MGMW_E_0011')
    ) {
      dispatch({ type: CREATE_ACCOUNT_CLEAR_DATA });
      errorPage = CREATE_ACCOUNT_STEPS.IDPROOFING_MAX_ATTEMPTS;
    }
    if (errorCodes.includes('MGMW_EX_9997')) {
      // Account creation cannot be completed
      errorPage = CREATE_ACCOUNT_STEPS.DISABLED_USER;
    }
    if (errorCodes.includes('MGMW_EX_0000')) {
      //STRY0285774 - Disabled SSN9 account — show disabled user error screen
      errorPage = CREATE_ACCOUNT_STEPS.DISABLED_USER;
    } else if (errorCodes.includes('MGMW_E_0002')) {
      const existingEmailRefId = response.headersObject['x-req-id'];
      const email = body.data && body.data.maskedEmail;
      if (email) {
        // Active account with same email (GUID collision) — show account exists screen
        await dispatch({
          type: CREATE_ACCOUNT_CAPTURE_EMAIL_USER_EXISTS,
          payload: { email, existingEmailRefId },
        });
        errorPage = CREATE_ACCOUNT_STEPS.ACCOUNT_DEFINITELY_EXISTS;
      } else {
        // Disabled SSN4/custom account — show disabled user error screen
        errorPage = CREATE_ACCOUNT_STEPS.DISABLED_USER;
      }
    }
    if (errorCodes.includes('MGMW_E_0099')) {
      state = body.errors.REF && body.errors.REF[0];
      errorPage = CREATE_ACCOUNT_STEPS.ACCOUNT_MAY_EXIST;
    }
    if (errorCodes.includes('MGMW_E_0004')) {
      state = body.errors.REF && body.errors.REF[0];
      errorPage = CREATE_ACCOUNT_STEPS.FAILED_IDPROOFING;
    }

    if (errorPage) {
      const pii = getState().createAccount.pii;
      return dispatch(push(getCreateAccountPath(errorPage, pii), state));
    } else {
      return handleRequestFailure(body, formikActions);
    }
  }

  if (body.status === 'CONTINUE') {
    const {
      personalInformation = {},
      licenseClass = '',
      licenseType = '',
      expirationDate = '',
    } = (body && body.data) || {};
    await dispatch({
      type: CREATE_ACCOUNT_CAPTURE_VERIFY,
      payload: {
        verifyToken: body.message,
        verifyData: {
          personalInformation,
          licenseClass,
          licenseType,
          expirationDate,
        },
      },
    });

    const pii = getState().createAccount.pii;
    return dispatch(
      push(getCreateAccountPath(CREATE_ACCOUNT_STEPS.SUCCESS, pii))
    );
  }

  if (body.status === 'SUCCESS' && body && body.data) {
    const {
      personalInformation = {},
      licenseClass = '',
      licenseType = '',
      expirationDate = '',
    } = body.data;
    await dispatch({
      type: CREATE_ACCOUNT_CAPTURE_VERIFY,
      payload: {
        verifyToken: body.message,
        verifyData: {
          personalInformation,
          licenseClass,
          licenseType,
          expirationDate,
        },
      },
    });

    const pii = getState().createAccount.pii;
    return dispatch(
      push(getCreateAccountPath(CREATE_ACCOUNT_STEPS.SUCCESS, pii))
    );
  }
};

export const createUserAccount = formikActions => async (
  dispatch,
  getState
) => {
  const { user, verifyToken } = getState().createAccount;

  const [{ response, body }, err] = await to(
    dispatch(
      createAccountCreateUserRequest({
        userSessionToken: verifyToken,
      })
    )
  );

  if (backend.isServerError({ body, response })) {
    return dispatch({ type: errorActions.SERVER_ERROR, payload: body });
  }

  if (err) {
    return Promise.reject(err);
  }

  const failure = errorsWithOnlyCodes(((body && body.errors) || {}).ERROR);

  if (checkCreateAccountFailure(failure)) {
    const errorId = response.headersObject['x-req-id'];
    body.errors = {
      ...(errorId && { REF: [errorId] }),
      ERROR: ['CREATE_ACCOUNT_ERROR'],
    };
    return handleRequestFailure(body, formikActions);
  }

  if (body && body.message === 'MGMW_EX_3015') {
    body.errors = {
      ...body.errors,
      ERROR: ['MGMW_EX_3015'],
    };
  }

  if (checkRequestFailure({ response, body })) {
    const isDLLinkFailure = body && body.message === 'MGMW_EX_3015';
    if (isDLLinkFailure) {
      return dispatch(
        push(
          `/create-account/${CREATE_ACCOUNT_STEPS.CREATE_ACCOUNT_SUCCESS_LINKING_FAILED}`
        )
      );
    } else {
      return handleRequestFailure(body, formikActions);
    }
  }

  formikActions.setSubmitting(false);
  const isCreateUserSuccess =
    body && (body.status === 'SUCCESS' || body.message === 'REACTIVATED');

  if (isCreateUserSuccess) {
    return dispatch(
      handleCreateAccountSuccess({
        email: user.userEmail,
        firstName: user.fName,
        reactivated: body.message === 'REACTIVATED',
      })
    );
  }
};
